import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Users, MessageSquare, Search, Briefcase, ArrowRight, Network } from 'lucide-react';

const Landing = () => {
  const features = [
    {
      icon: Users,
      title: 'Build Your Network',
      description: 'Connect with sales and HR professionals who share your goals and aspirations.'
    },
    {
      icon: MessageSquare,
      title: 'Direct Messaging',
      description: 'Have meaningful conversations with your connections in a professional setting.'
    },
    {
      icon: Search,
      title: 'Discover Talent',
      description: 'Find the right people with powerful search by role, company, and expertise.'
    },
    {
      icon: Briefcase,
      title: 'Share Insights',
      description: 'Post updates, share knowledge, and engage with content from your network.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="navbar px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center">
              <Network className="w-6 h-6 text-white" />
            </div>
            <span className="text-2xl font-bold text-foreground font-serif">net-o-pad</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/login">
              <Button variant="ghost" className="rounded-full px-6" data-testid="nav-login-btn">
                Log in
              </Button>
            </Link>
            <Link to="/signup">
              <Button className="rounded-full px-6 bg-primary hover:bg-primary/90" data-testid="nav-signup-btn">
                Get Started
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero-gradient py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fadeIn">
              <span className="inline-block px-4 py-2 bg-secondary text-secondary-foreground rounded-full text-sm font-semibold mb-6">
                For Sales & HR Professionals
              </span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground mb-6 leading-tight">
                Your Networking
                <span className="text-primary"> Pad</span>
                <br />For Growth
              </h1>
              <p className="text-lg text-muted-foreground mb-8 max-w-xl">
                Connect, share, grow and build meaningful relationships. 
                net-o-pad is where sales and HR professionals thrive together.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/signup">
                  <Button size="lg" className="rounded-full px-8 bg-primary hover:bg-primary/90 btn-interact" data-testid="hero-signup-btn">
                    Join net-o-pad
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/login">
                  <Button size="lg" variant="outline" className="rounded-full px-8 btn-interact" data-testid="hero-login-btn">
                    I have an account
                  </Button>
                </Link>
              </div>
            </div>
            <div className="hidden lg:block animate-fadeIn stagger-2">
              <div className="relative">
                <div className="absolute inset-0 bg-primary/10 rounded-3xl transform rotate-3"></div>
                <img 
                  src="https://images.unsplash.com/photo-1758518729685-f88df7890776?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBvZmZpY2UlMjBtZWV0aW5nfGVufDB8fHx8MTc2ODYyMzEzNHww&ixlib=rb-4.1.0&q=85&w=800"
                  alt="Professional networking"
                  className="relative rounded-3xl shadow-2xl w-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
              Everything You Need to Succeed
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Powerful features designed specifically for sales and HR professionals to network effectively.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div 
                key={index} 
                className="feature-card animate-fadeIn"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="w-14 h-14 rounded-2xl bg-secondary flex items-center justify-center mb-6">
                  <feature.icon className="w-7 h-7 text-primary" />
                </div>
                <h3 className="text-xl font-bold text-foreground mb-3">{feature.title}</h3>
                <p className="text-muted-foreground">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-primary">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div className="animate-fadeIn">
              <div className="text-4xl lg:text-5xl font-bold text-white mb-2">10K+</div>
              <div className="text-primary-foreground/80">Active Professionals</div>
            </div>
            <div className="animate-fadeIn stagger-1">
              <div className="text-4xl lg:text-5xl font-bold text-white mb-2">50K+</div>
              <div className="text-primary-foreground/80">Connections Made</div>
            </div>
            <div className="animate-fadeIn stagger-2">
              <div className="text-4xl lg:text-5xl font-bold text-white mb-2">1K+</div>
              <div className="text-primary-foreground/80">Companies Represented</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-32">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-6">
            Ready to Grow Your Network?
          </h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
            Join thousands of sales and HR professionals who are already building meaningful connections on net-o-pad.
          </p>
          <Link to="/signup">
            <Button size="lg" className="rounded-full px-10 bg-primary hover:bg-primary/90 btn-interact" data-testid="cta-signup-btn">
              Create Your Profile
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-border">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
                <Network className="w-4 h-4 text-white" />
              </div>
              <span className="text-lg font-bold text-foreground font-serif">net-o-pad</span>
            </div>
            <p className="text-sm text-muted-foreground">
              © 2025 net-o-pad. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
